import React from 'react'
import PropTypes from 'prop-types'
import { makeStyles, Tooltip } from '@material-ui/core'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { ExplainabilitySection, FinalMessageBlock } from '../Explainability'
import Icon from '../../../../components/atoms/Icon'
import SydButton from '../../../../components/commonDesign/Button'
import FlexRow from '../../../../components/molecules/FlexRow'
import { getAttachmentIcon } from '../utils/attachmentUtils'
import InlineAbundanceRenderer from './InlineAbundanceRenderer'
import MessageGenerating from './MessageGenerating'
import InlineActions from './InlineActions'

const useStyles = makeStyles((theme) => ({
  content: {
    fontSize: 'var(--font-size-sm)',
    color: 'var(--text-color-main)',
    fontWeight: 'var(--font-weight-light)',
    lineHeight: 1.6,
    '& p': {
      fontSize: 'var(--font-size-sm)',
      margin: '0 0 12px 0',
      '&:last-child': {
        margin: 0
      }
    },
    '& ul, & ol': {
      margin: '0 0 12px 0',
      paddingLeft: '24px',
      '&:last-child': {
        margin: 0
      }
    },
    '& li': {
      marginBottom: '4px'
    },
    '& code': {
      backgroundColor: 'var(--surface-bg-color-d1)',
      padding: '2px 6px',
      borderRadius: '4px',
      fontSize: '13px',
      fontFamily: 'var(--font-family-monospace)'
    },
    '& pre': {
      border: 'var(--border-neutral-thin)',
      backgroundColor: 'var(--surface-bg-color-d2)',
      paddingLeft: 'var(--padding-sm)',
      paddingRight: 'var(--padding-sm)',
      borderRadius: '8px',
      overflow: 'auto',
      margin: '12px 0',
      '& code': {
        backgroundColor: 'transparent',
        padding: 0
      }
    },
    '& blockquote': {
      borderLeft: '3px solid var(--color-primary-500)',
      paddingLeft: '12px',
      margin: '12px 0',
      fontStyle: 'italic',
      color: 'var(--color-neutral-300)'
    },
    '& a': {
      color: 'var(--color-primary-500)',
      textDecoration: 'none',
      fontWeight: 500,
      borderBottom: '1px solid var(--color-primary-500)',
      transition: 'all 0.2s ease',
      '&:hover': {
        color: 'var(--color-primary-400)',
        borderBottomColor: 'var(--color-primary-400)'
      },
      '&:visited': {
        color: 'var(--color-primary-600)',
        borderBottomColor: 'var(--color-primary-600)'
      }
    }
  },
  attachments: {
    marginTop: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  attachment: {
    padding: '12px 16px',
    backgroundColor: 'var(--surface-bg-color-d2)',
    border: '1px solid var(--color-neutral-600)',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  attachmentInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  attachmentIcon: {
    fontSize: '24px',
    color: 'var(--color-primary-500)'
  },
  attachmentDetails: {
    display: 'flex',
    flexDirection: 'column'
  },
  attachmentName: {
    fontWeight: 600,
    fontSize: '14px',
    color: 'var(--color-neutral-200)'
  },
  attachmentType: {
    fontSize: '12px',
    color: 'var(--color-neutral-400)'
  },
  codeBlock: {
    position: 'relative',
    '& pre': {
      paddingTop: '36px'
    }
  },
  codeHeader: {
    position: 'absolute',
    top: '12px',
    left: '12px',
    right: '12px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  codeLanguage: {
    fontSize: '12px',
    color: 'var(--color-neutral-400)',
    textTransform: 'uppercase'
  },
  emptyMessage: {
    fontStyle: 'italic',
    color: 'var(--color-neutral-500)'
  },
  userAttachments: {
    marginTop: '12px',
    display: 'flex',
    flexDirection: 'row',
    gap: '8px',
    alignItems: 'center'
  },
  userAttachmentIcon: {
    fontSize: '18px',
    color: 'var(--dai-color-text-low)',
    cursor: 'default'
  }
}))

function MessageContent ({ parentInfo, message, showExplainability = true, isGenerating = false, onSuggestionClick, onReply }) {
  const classes = useStyles()

  // For assistant messages
  if (message.actor !== 'user') {
    return (
      <>
        <MessageGenerating message={message} />

        {/* Render explainability events - always show during generation, otherwise respect showExplainability */}
        <ExplainabilitySection
          parentInfo={parentInfo}
          messageId={message.threadMessageId}
          visible={showExplainability}
          isGenerating={isGenerating}
          toolCallSummary={message.data?.toolCallSummary}
        />

        {/* Final message at the bottom */}
        {message.message ? (
          <FinalMessageBlock
            content={message.message}
            timestamp={message.createdDate}
            onSuggestionClick={onSuggestionClick}
            message={message}
            onReply={onReply}
          />
        ) : null}
      </>
    )
  }

  // Handle empty message
  if (!message.message && (!message.attachments || message.attachments.length === 0)) {
    return (
      <div className={classes.content}>
        <span className={classes.emptyMessage}>No content</span>
      </div>
    )
  }

  const handleCodeAction = (code, language) => {
    if (language === 'dql' || language === 'sql') {
      // Open in query editor with query pre-filled
      const queryToolPath = '/admin/denali/analysis/catalog/query'
      // Store query in sessionStorage to pass to editor
      sessionStorage.setItem('denaliAssistantQuery', code)
      sessionStorage.setItem('denaliAssistantQueryType', language)
      window.open(queryToolPath, '_blank')
    } else {
      // Copy to clipboard
      navigator.clipboard.writeText(code)
      // TODO: Show toast notification
    }
  }

  // Custom renderers for markdown
  const renderers = {
    pre ({ children }) {
      // Extract the code element and its props
      const codeElement = children.props ? children : children[0]
      const className = codeElement.props?.className || ''
      const codeString = String(codeElement.props?.children || '').replace(/\n$/, '')

      // Check if this is an abundance engine chart
      // The markdown parser might only capture 'jsx' from 'jsx/abundance-engine'
      // So we also check the content for InlineChart components
      const match = /language-(.*)/.exec(className)
      const language = match ? match[1] : ''
      // Detect abundance engine charts and tables by multiple methods:
      // 1. Language is exactly 'jsx/abundance-engine'
      // 2. Language starts with 'jsx' and content has InlineChart or InlineTable
      // 3. Any code block with InlineChart or InlineTable component (fallback)
      const isAbundanceChart =
        language === 'jsx-abundance-engine' ||
        (language.startsWith('jsx') && (codeString.includes('<InlineChart') || codeString.includes('<InlineTable'))) ||
        (codeString.includes('<InlineChart') && codeString.includes('options={{')) ||
        codeString.includes('<InlineTable')

      if (isAbundanceChart) {
        return <InlineAbundanceRenderer code={codeString} />
      }

      if (language === 'denali-ai-actions') {
        // Ensure message is available - it should be from closure
        if (!message) {
          console.error('MessageContent: message is undefined in denali-ai-actions renderer!')
        }
        return <InlineActions content={codeString} onSuggestionClick={onSuggestionClick} message={message} />
      }

      if (language) {
        return (
          <div className={classes.codeBlock}>
            <div className={classes.codeHeader}>
              <span className={classes.codeLanguage}>{language.split('/')[0]}</span>
              <FlexRow gap='xs'>
                {(language === 'dql' || language === 'sql') && (
                  <SydButton
                    size='xs'
                    variant='ghost'
                    onClick={() => handleCodeAction(codeString, language)}
                  >
                    <Icon name='open_in_new' style={{ fontSize: '14px' }} />
                    Open in Editor
                  </SydButton>
                )}
                <SydButton
                  size='xs'
                  variant='ghost'
                  onClick={() => navigator.clipboard.writeText(codeString)}
                >
                  <Icon name='content_copy' style={{ fontSize: '14px' }} />
                  Copy
                </SydButton>
              </FlexRow>
            </div>
            <pre className={className}>
              <code>{codeString}</code>
            </pre>
          </div>
        )
      }

      // Fall back to default pre rendering for code blocks without language
      return <pre>{children}</pre>
    },
    code ({ inline, children, ...props }) {
      // Only handle inline code here
      if (inline) {
        return <code {...props}>{children}</code>
      }
      // Block code is handled by the pre renderer above
      return <code {...props}>{children}</code>
    }
  }

  // Handle empty message
  if (!message.message && (!message.attachments || message.attachments.length === 0)) {
    return (
      <div className={classes.content}>
        <span className={classes.emptyMessage}>No content</span>
      </div>
    )
  }

  // For user messages or agent messages without events, show traditional layout
  return (
    <>
      <div className={classes.content}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={renderers}
        >
          {message.message}
        </ReactMarkdown>
      </div>
      {message.attachments && Array.isArray(message.attachments) && message.attachments.length > 0 && (
        <div className={classes.userAttachments}>
          {message.attachments.map((attachment, index) => {
            if (!attachment) return null
            // For user-uploaded files (with data.key), always use 'file' icon
            // For AI-generated attachments, use getAttachmentIcon
            const isUserUploadedFile = attachment.data && attachment.data.key
            const iconName = isUserUploadedFile ? 'file' : (getAttachmentIcon(attachment.contentType) || 'file')
            return (
              <Tooltip key={index} title={attachment.title || 'Attachment'} arrow>
                <span>
                  <Icon
                    name={iconName}
                    style={{ fontSize: '18px', color: 'var(--dai-color-text-low)' }}
                  />
                </span>
              </Tooltip>
            )
          })}
        </div>
      )}
    </>
  )
}

MessageContent.propTypes = {
  parentInfo: PropTypes.shape({
    parentId: PropTypes.string,
    parentType: PropTypes.oneOf(['schedule', 'thread'])
  }),
  message: PropTypes.shape({
    threadMessageId: PropTypes.string,
    actor: PropTypes.oneOf(['user', 'assistan']),
    message: PropTypes.string,
    attachments: PropTypes.array,
    isGenerating: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    createdDate: PropTypes.string,
    data: PropTypes.shape({
      events: PropTypes.array,
      lastEventSequence: PropTypes.number,
      lastUpdateTimestamp: PropTypes.string,
      currentStepId: PropTypes.string,
      toolCallSummary: PropTypes.shape({
        dataSources: PropTypes.arrayOf(PropTypes.shape({
          type: PropTypes.oneOf(['view', 'api']),
          name: PropTypes.string,
          count: PropTypes.number
        })),
        totalSources: PropTypes.number,
        totalAccesses: PropTypes.number,
        toolCallCount: PropTypes.number
      })
    })
  }).isRequired,
  showExplainability: PropTypes.bool,
  isGenerating: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
  onSuggestionClick: PropTypes.func,
  onReply: PropTypes.func
}

export default MessageContent
